import { Autocomplete, Box, Grid, Stack, Switch, TextField } from "@mui/material";
import { isEmpty } from "lodash";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import CustomCard from "components/CustomCard";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import updateCompany from "api/companies/updateCompany";
import FlexWrapper from "components/Wrapper/FlexWrapper";
import { useAppContextController } from "context/AppContext";
import useCompanyStore from "stores/useCompanyStore";
import PanelActions from "components/PanelActions";
import { AutoAwesome } from "@mui/icons-material";
import MDTypography from "components/MDTypography";
import CustomTextField from "components/Form/CustomTextField";
import useDropdowns from "hooks/useDropdowns";
import { getDirtyFields } from "utils/helpers/formHelpers";
import { DEFAULT_CUSTOMER } from "data/companyInfo";
import FormTextEditor from "components/FormTextEditor";
import GenericModal from "components/GenericModal";
import parse from "html-react-parser";
import {
  AI_INTERVIEWS_FIRST_MESSAGE,
  AI_INTERVIEWS_LAST_MESSAGE,
} from "utils/constants/aiInterviews";

import { COMPANY_ENTITIES } from "utils/constants/entityOptions";
import { NumericFormat } from "react-number-format";
import FormField from "layouts/applications/wizard/components/FormField";
import EditTime from "layouts/pages/events/components/EditTime";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import useYupValidationResolver from "hooks/useYupValidationResolver";
import { primaryCompanyAISettingsSchema } from "data/primaryCompanyAISettings";

const { AI_EDUCATION_LEVEL } = COMPANY_ENTITIES;

const dropdownOptionsVenues = [
  {
    label: "None",
    value: "",
  },
  {
    label: "Venues",
    value: "venues",
  },
  {
    label: "Venues and Jobs",
    value: "venues,jobs",
  },
];

const dropdownOptionsCustomers = [
  {
    label: "None",
    value: "",
  },
  {
    label: "Customers",
    value: "customers",
  },
  {
    label: "Customers and Jobs",
    value: "customers,jobs",
  },
];

const dropdownOptionsMinScore = [
  {
    label: "10%",
    value: "10",
  },
  {
    label: "25%",
    value: "25",
  },
  {
    label: "50%",
    value: "50",
  },
  {
    label: "70%",
    value: "70",
  },
  {
    label: "80%",
    value: "80",
  },
  {
    label: "90%",
    value: "90",
  },
  {
    label: "100%",
    value: "100",
  },
];

const CompanyAISettingsForm = () => {
  const { setCompanyFormState, companyFormState, setCurrentCompany, currentCompany } =
    useCompanyStore((state) => ({
      setCompanyFormState: state.setCompanyFormState,
      companyFormState: state.companyFormState,
      setCurrentCompany: state.setCurrentCompany,
      currentCompany: state.currentCompany,
    }));

  const { company, setCompany, companyType, isMobile } = useAppContextController();
  const isCompany = companyType !== "Venue";
  const [previewMessage, setPreviewMessage] = useState(null);

  const isEdit = !!currentCompany?._id;

  const { dropdowns: educationLevels } = useDropdowns({ entity: AI_EDUCATION_LEVEL });

  const aiEnableDefaultOptions = isCompany ? dropdownOptionsCustomers : dropdownOptionsVenues;

  // const shouldDisableAutoScheduling = process.env.REACT_APP_ENV?.trim() === "production";
  const shouldDisableAutoScheduling = false;

  const { enqueueSnackbar } = useSnackbar();
  const resolver = useYupValidationResolver(primaryCompanyAISettingsSchema);

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    watch,
    reset,
    setError,
    trigger,
    formState,
  } = useForm({
    defaultValues: {
      aiSettings: {
        aiInterviewsFirstMessage: AI_INTERVIEWS_FIRST_MESSAGE,
        aiInterviewsLastMessage: AI_INTERVIEWS_LAST_MESSAGE,
        defaultEducationLevel: "High School",
      },
    },
    resolver,
  });
  const { errors, isDirty, isValid, isSubmitting, isSubmitSuccessful, dirtyFields } = formState;

  const { mutateAsync: updateCompanyMutation } = useMutation(updateCompany);
  const updateCompanyHandler = (data) => {
    const body = {
      ...getDirtyFields(data, dirtyFields),
    };
    if (body.aiSettings.maxReminderCount && typeof body.aiSettings.maxReminderCount === "string")
      body.aiSettings.maxReminderCount = parseInt(body.aiSettings.maxReminderCount, 10);
    updateCompanyMutation(
      {
        companyId: currentCompany?._id,
        body,
      },
      {
        onSuccess: () => {
          setCurrentCompany({ ...currentCompany, ...data });
          if (currentCompany?.primaryCompany) {
            setCompany({ ...company, ...data });
          }
          enqueueSnackbar("Company has been updated.", { variant: "success" });
          reset(data, { keepValues: true });
        },
      }
    );
  };

  useEffect(() => {
    if (!isEmpty(currentCompany)) {
      delete currentCompany?.longitude;
      delete currentCompany?.latitude;
      delete currentCompany?.radius;
    }

    reset(isEdit ? currentCompany : DEFAULT_CUSTOMER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, isEdit]);

  useEffect(() => {
    setCompanyFormState({ ...companyFormState, isDirty, dirtyFields });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, dirtyFields]);

  const handleCancelChanges = () => reset(currentCompany);

  const handleOpenPreviewModal = (message) => setPreviewMessage(message);
  const handleClosePreviewModal = () => setPreviewMessage(null);

  const defaultMinAvailabilityTime = useMemo(
    () => moment().set("hours", 6).set("minutes", 0).set("seconds", 0).set("milliseconds", 0),
    []
  );
  const defaultMaxAvailabilityTime = useMemo(
    () => moment().set("hours", 21).set("minutes", 0).set("seconds", 0).set("milliseconds", 0),
    []
  );

  const formatTimeForField = (timePart) => {
    if (timePart) {
      return moment(`${new Date().toISOString().split("T")[0]}T${timePart}`);
    }
    return timePart;
  };

  return (
    <form
      onSubmit={handleSubmit(updateCompanyHandler)}
      id="company-setting-form"
      style={{ width: "100%" }}
    >
      <CustomCard
        icon={<AutoAwesome color="white" />}
        iconContainerSx={{ backgroundColor: "violet", mx: "1.5rem", mt: "-1.5rem" }}
        cardSx={{ width: "100%", overflow: "visible !important", p: 0, height: 850 }}
        cardActions={
          isDirty && !isEmpty(dirtyFields) && <PanelActions onCancel={handleCancelChanges} />
        }
        cardTitle="Company AI Settings"
        actionsSx={isMobile ? {} : { mt: -4, mr: 4 }}
      >
        <Box sx={{ height: 750, overflowY: "auto", p: "1.5rem", pt: 1 }}>
          <FlexWrapper justifyContent="space-between">
            <Grid container alignItems="center" spacing={3} pt={2}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item sm={12}>
                    <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
                      AI Configurations
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack
                      my={-1.5}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <MDTypography variant="body2">AI Interviews Enabled</MDTypography>
                      <Controller
                        name="aiSettings.aiInterviewsEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            color="info"
                            checked={field.value === "Yes"}
                            onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                          />
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid container item xs={12} sm={6} alignItems="center">
                    <Stack
                      my={-1.5}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexGrow={1}
                    >
                      <MDTypography variant="body2">
                        AI Screening Finished Notifications
                      </MDTypography>
                      <Controller
                        name="aiSettings.notificationsAiScreeningEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value === "Yes"}
                            onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                          />
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Stack
                      my={-1.5}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <MDTypography variant="body2">{`Auto-generate ${
                        isCompany ? "Customer" : "Venue"
                      } Questions`}</MDTypography>
                      <Controller
                        name="aiSettings.autoGenerateCustomerQuestions"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value === "Yes"}
                            onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                          />
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid container item xs={12} sm={6} alignItems="center">
                    <Stack
                      my={-1.5}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      flexGrow={1}
                    >
                      <MDTypography variant="body2">Waiting For Call Notifications</MDTypography>
                      <Controller
                        name="aiSettings.notificationsWaitingForCallEnabled"
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={field.value === "Yes"}
                            onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                          />
                        )}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="aiSettings.aiInterviewsEnabledByDefault"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          options={aiEnableDefaultOptions || []}
                          name="aiSettings.aiInterviewsEnabledByDefault"
                          autoSelect
                          getOptionLabel={(option) => option.label || option}
                          value={
                            field.value != null
                              ? aiEnableDefaultOptions?.find(
                                  (option) => option.value === field.value
                                )
                              : aiEnableDefaultOptions[0]
                          }
                          isOptionEqualToValue={(option, value) => option.value === value}
                          onChange={(e, v) => {
                            setValue("aiSettings.aiInterviewsEnabledByDefault", v.value, {
                              shouldDirty: true,
                            });
                          }}
                          renderInput={(params) => (
                            <CustomTextField
                              type="text"
                              label="AI Interviews Enabled by Default"
                              showError
                              errorMessage={error?.message}
                              {...params}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="aiSettings.defaultEducationLevel"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <Autocomplete
                          {...field}
                          options={educationLevels || []}
                          name="aiSettings.defaultEducationLevel"
                          autoSelect
                          getOptionLabel={(option) => option}
                          isOptionEqualToValue={(option, value) => option === value}
                          onChange={(e, v) => {
                            setValue("aiSettings.defaultEducationLevel", v, {
                              shouldDirty: true,
                            });
                          }}
                          renderInput={(params) => (
                            <CustomTextField
                              type="text"
                              label="Default education level"
                              showError
                              errorMessage={error?.message}
                              {...params}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="aiSettings.maxReminderCount"
                      control={control}
                      defaultValue={3}
                      render={({ field }) => (
                        <NumericFormat
                          InputProps={{
                            inputProps: {
                              // style: { textAlign: "end" },
                              inputMode: "numeric",
                              type: "number",
                            },
                          }}
                          onValueChange={({ floatValue }) =>
                            setValue("aiSettings.maxReminderCount", floatValue, {
                              shouldDirty: true,
                            })
                          }
                          label="Max Reminders for ATC"
                          decimalScale={0}
                          value={field.value || 0}
                          {...field}
                          customInput={FormField}
                        />
                      )}
                    />
                    {errors?.["additional.minPayRate"] && (
                      <MDTypography color="error">
                        {errors?.["additional.minPayRate"].message}
                      </MDTypography>
                    )}
                  </Grid>
                  <Grid item xs={0} sm={6} />
                  {shouldDisableAutoScheduling ? null : (
                    <>
                      <Grid item sm={12}>
                        <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
                          Interview Auto-Scheduling
                        </MDTypography>
                      </Grid>
                      <Grid container item xs={12} alignItems="center" spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Stack
                            my={-1.5}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <MDTypography variant="body2">
                              Enable Interview Auto-Scheduling
                            </MDTypography>
                            <Controller
                              name="aiSettings.interviewAutoSchedulingEnabled"
                              control={control}
                              render={({ field }) => (
                                <Switch
                                  color="info"
                                  checked={field.value === "Yes"}
                                  onChange={(e) => field.onChange(e.target.checked ? "Yes" : "No")}
                                />
                              )}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="aiSettings.interviewAutoSchedulingMinScore"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                              <Autocomplete
                                options={dropdownOptionsMinScore || []}
                                name="aiSettings.interviewAutoSchedulingMinScore"
                                autoSelect
                                disabled={
                                  watch("aiSettings.interviewAutoSchedulingEnabled") !== "Yes"
                                }
                                getOptionLabel={(option) => option.label || option}
                                value={
                                  field.value != null
                                    ? dropdownOptionsMinScore?.find(
                                        (option) => option.value === field.value
                                      )
                                    : dropdownOptionsMinScore[3]
                                }
                                isOptionEqualToValue={(option, value) => option.value === value}
                                onChange={(e, v) => {
                                  setValue("aiSettings.interviewAutoSchedulingMinScore", v.value, {
                                    shouldDirty: true,
                                  });
                                }}
                                renderInput={(params) => (
                                  <CustomTextField
                                    type="text"
                                    label="Min. Score To Schedule"
                                    showError
                                    errorMessage={error?.message}
                                    {...params}
                                  />
                                )}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid container item xs={12} alignItems="center" spacing={2} mt={0}>
                        <Grid item xs={12} sm={6}>
                          <Stack
                            my={-1.5}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <MDTypography variant="body2">Min time for availability</MDTypography>
                            <Controller
                              name="aiSettings.interviewAutoSchedulingMinAvailabilityTime"
                              control={control}
                              render={({ field }) => (
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                  <TimePicker
                                    views={["hours"]}
                                    value={
                                      formatTimeForField(field.value) || defaultMinAvailabilityTime
                                    }
                                    onChange={(newTime) => {
                                      if (!newTime.isValid()) return;

                                      const newTimeAdjusted = newTime
                                        .set("minutes", 0)
                                        .set("seconds", 0);

                                      const newTimeFormatted = newTimeAdjusted
                                        .toISOString()
                                        .split("T")[1];

                                      setValue(
                                        "aiSettings.interviewAutoSchedulingMinAvailabilityTime",
                                        newTimeFormatted,
                                        {
                                          shouldDirty: true,
                                        }
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        sx={{ input: { textAlign: "center" } }}
                                        style={{ width: "30%" }}
                                        {...params}
                                        inputProps={{
                                          ...params.inputProps,
                                          readOnly: true,
                                        }}
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              )}
                            />
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack
                            my={-1.5}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <MDTypography variant="body2">Max time for availability</MDTypography>
                            <Controller
                              name="aiSettings.interviewAutoSchedulingMaxAvailabilityTime"
                              control={control}
                              render={({ field }) => (
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                  <TimePicker
                                    views={["hours"]}
                                    value={
                                      formatTimeForField(field.value) || defaultMaxAvailabilityTime
                                    }
                                    onChange={(newTime) => {
                                      if (!newTime.isValid()) return;

                                      const newTimeAdjusted = newTime
                                        .set("minutes", 0)
                                        .set("seconds", 0);

                                      const newTimeFormatted = newTimeAdjusted
                                        .toISOString()
                                        .split("T")[1];

                                      setValue(
                                        "aiSettings.interviewAutoSchedulingMaxAvailabilityTime",
                                        newTimeFormatted,
                                        {
                                          shouldDirty: true,
                                        }
                                      );
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        sx={{ input: { textAlign: "center" } }}
                                        style={{ width: "30%" }}
                                        {...params}
                                        inputProps={{
                                          ...params.inputProps,
                                          readOnly: true,
                                        }}
                                      />
                                    )}
                                  />
                                </LocalizationProvider>
                              )}
                            />
                          </Stack>
                        </Grid>
                      </Grid>
                      {errors?.["aiSettings.interviewAutoSchedulingMinAvailabilityTime"] && (
                        <MDTypography ml={2} mt={1} color="error" variant="body2">
                          {
                            errors?.["aiSettings.interviewAutoSchedulingMinAvailabilityTime"]
                              .message
                          }
                        </MDTypography>
                      )}
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormTextEditor
                      title="First message"
                      name="aiSettings.aiInterviewsFirstMessage"
                      key="aiSettings.aiInterviewsFirstMessage"
                      onClick={() =>
                        handleOpenPreviewModal(watch("aiSettings.aiInterviewsFirstMessage"))
                      }
                      control={control}
                      height="6rem"
                      preserveWhitespace
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormTextEditor
                      title="Last message"
                      name="aiSettings.aiInterviewsLastMessage"
                      key="aiSettings.aiInterviewsLastMessage"
                      onClick={() =>
                        handleOpenPreviewModal(watch("aiSettings.aiInterviewsLastMessage"))
                      }
                      control={control}
                      height="6rem"
                      preserveWhitespace
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </FlexWrapper>
        </Box>
      </CustomCard>
      {!!previewMessage && (
        <GenericModal
          open={!!previewMessage}
          setOpen={handleClosePreviewModal}
          body={<Box p={3}>{parse(previewMessage)}</Box>}
          borderRadius={5}
          overflow="hidden"
          contentPadding={0}
          width="90%"
          maxWidth={1170}
          maxHeight={940}
        />
      )}
    </form>
  );
};

export default CompanyAISettingsForm;
